import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Chat Widget Entry Point
 *
 * WordPress AI Chatbot Pro - Frontend Chat Widget
 *
 * @package WooAIChatbot
 */
import React from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import { ChatWidgetBottom } from './components/ChatWidgetBottom';
import { ChatProvider } from '../contexts/ChatContext';
import { logger } from '../utils/logger';
import { initFromWordPress, trackEvent } from '../analytics';
import './styles.css';
/**
 * Card shadow presets
 */
const CARD_SHADOW_PRESETS = {
    none: 'none',
    small: '0 1px 2px rgba(0, 0, 0, 0.05)',
    medium: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    large: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
};
/**
 * Apply appearance settings to CSS custom properties
 */
const applyAppearanceSettings = (container) => {
    const config = window.wooAIChatConfig;
    const appearance = config?.appearance;
    if (!appearance) {
        logger.debug('No appearance config found, using defaults');
        return;
    }
    logger.debug('Applying appearance settings:', appearance);
    // Apply primary color and derived colors
    if (appearance.primaryColor) {
        container.style.setProperty('--waa-primary', appearance.primaryColor);
        // Generate lighter/darker variants
        container.style.setProperty('--waa-primary-light', adjustColor(appearance.primaryColor, 20));
        container.style.setProperty('--waa-primary-dark', adjustColor(appearance.primaryColor, -20));
        container.style.setProperty('--waa-primary-50', adjustColor(appearance.primaryColor, 90));
        container.style.setProperty('--waa-chat-user-bg', appearance.primaryColor);
    }
    // Apply secondary color
    if (appearance.secondaryColor) {
        container.style.setProperty('--waa-secondary', appearance.secondaryColor);
        container.style.setProperty('--waa-secondary-light', adjustColor(appearance.secondaryColor, 20));
        container.style.setProperty('--waa-secondary-dark', adjustColor(appearance.secondaryColor, -20));
    }
    // Apply accent color
    if (appearance.accentColor) {
        container.style.setProperty('--waa-accent', appearance.accentColor);
        container.style.setProperty('--waa-accent-light', adjustColor(appearance.accentColor, 20));
        container.style.setProperty('--waa-accent-dark', adjustColor(appearance.accentColor, -20));
    }
    // Apply text color
    if (appearance.textColor) {
        container.style.setProperty('--waa-text', appearance.textColor);
        container.style.setProperty('--waa-text-primary', appearance.textColor);
    }
    // Apply background color
    if (appearance.backgroundColor) {
        container.style.setProperty('--waa-bg-primary', appearance.backgroundColor);
        container.style.setProperty('--waa-surface', appearance.backgroundColor);
    }
    // Apply card colors
    if (appearance.cardBackground) {
        container.style.setProperty('--waa-card-bg', appearance.cardBackground);
    }
    if (appearance.cardTextColor) {
        container.style.setProperty('--waa-card-text', appearance.cardTextColor);
    }
    // Apply card border radius
    if (appearance.cardBorderRadius !== undefined) {
        container.style.setProperty('--waa-card-radius', `${appearance.cardBorderRadius}px`);
    }
    // Apply card shadow
    if (appearance.cardShadow) {
        const shadowValue = CARD_SHADOW_PRESETS[appearance.cardShadow] || CARD_SHADOW_PRESETS.medium;
        container.style.setProperty('--waa-card-shadow', shadowValue);
    }
    // Apply font family
    if (appearance.fontFamily) {
        container.style.setProperty('--waa-font-family', appearance.fontFamily);
        container.style.fontFamily = appearance.fontFamily;
    }
    // Apply font size scale
    if (appearance.fontSizeScale && appearance.fontSizeScale !== 1) {
        container.style.setProperty('--waa-font-scale', String(appearance.fontSizeScale));
        container.style.fontSize = `${appearance.fontSizeScale * 16}px`;
    }
    // Apply widget dimensions as CSS properties for component access
    if (appearance.widgetWidth) {
        container.style.setProperty('--waa-widget-width', `${appearance.widgetWidth}px`);
    }
    if (appearance.maxHeight) {
        container.style.setProperty('--waa-widget-max-height', `${appearance.maxHeight}px`);
    }
    // Apply custom CSS
    if (appearance.customCSS) {
        const styleId = 'woo-ai-chatbot-custom-css';
        let styleElement = document.getElementById(styleId);
        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }
        styleElement.textContent = appearance.customCSS;
    }
};
/**
 * Adjust color brightness
 * @param color - Hex color string
 * @param percent - Positive for lighter, negative for darker
 */
const adjustColor = (color, percent) => {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, Math.max(0, (num >> 16) + amt));
    const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
    const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};
/**
 * Export React to global scope for WordPress compatibility
 * Some WordPress themes/plugins expect React to be available globally
 */
if (typeof window !== 'undefined') {
    window.React = React;
    window.ReactDOM = ReactDOM;
}
/**
 * Chat Widget Wrapper Component
 * Default experience = Figma bottom dock flow
 */
const ChatWidget = () => (_jsx(ChatWidgetBottom, { initialState: "icon", initialConversation: "conversation" }));
/**
 * Initialize Chat Widget
 */
const initChatWidget = () => {
    logger.debug('Initializing Chat Widget...');
    logger.debug('Document ready state:', document.readyState);
    logger.debug('Window globals:', { wooAIChatbot: window.wooAIChatbot, wooAIChatConfig: window.wooAIChatConfig });
    // Validate that required config exists (prevents zombie widget after plugin reactivation without page refresh)
    const wooAI = window.wooAiChatbot || window.wooAIChatbot;
    if (!wooAI?.nonce && !window.wooAIChatConfig?.nonce) {
        logger.warn('No nonce found — plugin may have been reactivated. Page refresh required.');
        return;
    }
    // Initialize analytics from WordPress globals
    initFromWordPress();
    logger.debug('Analytics initialized from WordPress globals');
    // Create container if it doesn't exist
    let container = document.getElementById('woo-ai-chatbot-widget-root');
    logger.debug('Container found:', !!container);
    if (!container) {
        logger.debug('Creating new container div...');
        container = document.createElement('div');
        container.id = 'woo-ai-chatbot-widget-root';
    }
    // Always attach the root directly to <body> so theme markup (e.g. footer wrappers with transforms)
    // can't interfere with fixed positioning or introduce extra spacing.
    if (container.parentElement !== document.body) {
        logger.debug('Appending container to body...');
        document.body.appendChild(container);
    }
    else {
        logger.debug('Moving container to end of body...');
        // Ensure it's the last child for consistent stacking context.
        document.body.appendChild(container);
    }
    // Reset font-size to 16px to isolate from themes that override html font-size
    // (e.g. themes using vw-based rem scaling break all Tailwind rem utilities)
    container.style.fontSize = '16px';
    // Apply appearance settings before rendering
    applyAppearanceSettings(container);
    logger.debug('Creating React root...');
    const root = createRoot(container);
    logger.debug('Rendering Chat Widget...');
    root.render(_jsx(React.StrictMode, { children: _jsx(ChatProvider, { autoInit: true, children: _jsx(ChatWidget, {}) }) }));
    logger.debug('Chat Widget initialized successfully!');
    // Track widget loaded event
    trackEvent('widget_loaded', {
        category: 'chat',
        label: 'chat_widget',
        custom: {
            page_url: window.location.href,
            page_title: document.title,
        },
    });
};
// Initialize when DOM is ready
logger.debug('Chat widget script loaded. Ready state:', document.readyState);
if (document.readyState === 'loading') {
    logger.debug('Waiting for DOMContentLoaded...');
    document.addEventListener('DOMContentLoaded', initChatWidget);
}
else {
    logger.debug('DOM already ready, initializing now...');
    initChatWidget();
}
export default ChatWidget;
